import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Fab, Container, TextField, Switch, FormControlLabel, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { onSnapshot, doc, setDoc } from 'firebase/firestore';
import { db } from 'lib/firebase';
import AddOrEditDialog from './components/AddOrEditDialog';
import PageWrapper from '../../PageWrapper';
import AddressMap from './components/AddressMap';
import ExportDialog from './components/ExportDialog';

const CACHE_KEY = 'familyAddresses_cache';
const CACHE_TIMESTAMP_KEY = 'familyAddresses_cache_timestamp';

function FamilyAddressTable() {
  const [addresses, setAddresses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [currentData, setCurrentData] = useState({ lastName: '', address: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [mapView, setMapView] = useState(false);
  
  // Delete Confirmation State
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);


  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredAddresses = addresses.filter(row => {
    return row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.address2?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.state?.toLowerCase().includes(searchQuery.toLowerCase());
      });


  useEffect(() => {
    // Try to load from localStorage first
    const cachedData = localStorage.getItem(CACHE_KEY);
    const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (cachedData && cacheTimestamp) {
      const addressData = JSON.parse(cachedData);
      setAddresses(addressData);
    }

    // Set up Firebase listener
    const docRef = doc(db, 'familyAddresses', 'addresses');

    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const addressData = doc.data().data;
        void(0); // Log first address to see structure
        setAddresses(addressData);
        
        // Update localStorage cache
        localStorage.setItem(CACHE_KEY, JSON.stringify(addressData));
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      } else {
      }
    });

    // Clear cache when session ends (on page unload)
    const handleBeforeUnload = () => {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Clean up the listener when the component unmounts
    return () => {
      unsubscribe();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleRowClick = (data) => {
    setCurrentData(data);
    setDialogOpen(true);
  };

  const handleAddClick = () => {
    setCurrentData({ lastName: '', address: '' });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSave = async (updatedAddresses) => {
    try {
      // Reference to the document in Firestore
      const docRef = doc(db, 'familyAddresses', 'addresses');
  
      // Update the document with the new data
      await setDoc(docRef, { data: updatedAddresses });
      
      // Update localStorage cache immediately
      localStorage.setItem(CACHE_KEY, JSON.stringify(updatedAddresses));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  
    } catch (error) {
    }
  };

  const handleBatchUpdate = (addressesToUpdate) => {
    const updatedAllAddresses = [...addresses];
    addressesToUpdate.forEach(updatedAddress => {
        const index = updatedAllAddresses.findIndex(a => a.id === updatedAddress.id);
        if (index !== -1) {
            updatedAllAddresses[index] = updatedAddress;
        }
    });
    handleSave(updatedAllAddresses);
    handleSave(updatedAllAddresses);
  };

  // Delete Handlers
  const handleDeleteClick = (event, address) => {
    event.stopPropagation(); // Prevent row click from opening edit dialog
    setAddressToDelete(address);
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = () => {
    if (addressToDelete) {
      const updatedAddresses = addresses.filter(addr => addr.id !== addressToDelete.id);
      handleSave(updatedAddresses);
      setDeleteConfirmationOpen(false);
      setAddressToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmationOpen(false);
    setAddressToDelete(null);
  };


  return (
    <PageWrapper>
      <Container maxWidth="lg">
      {/* <div>
        {['Smith', 'Johnson', 'Williams'].map(chip => (
          <Chip
            key={chip}
            label={chip}
            onClick={() => handleFilterChange(chip)}
            style={{ margin: 2 }}
          />
        ))}  
      </div> */}

      {/* Address Table */}
      <FormControlLabel
        control={<Switch checked={mapView} onChange={() => setMapView(!mapView)} />}
        label="Map View"
      />
        
      <TextField
        label="Search"
        variant="outlined"
        value={searchQuery}
        onChange={handleSearchChange}
      />
      <Button 
        variant="contained" 
        color="secondary" 
        onClick={() => setExportDialogOpen(true)}
        sx={{ ml: 2 }}
      >
        Export Labels
      </Button>
      <Fab color="primary" aria-label="add" onClick={handleAddClick}>
        <AddIcon />
      </Fab>
      {mapView ? (
                <AddressMap addresses={filteredAddresses} onSave={handleBatchUpdate} onEdit={handleRowClick} />
      ) : (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>City</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAddresses.map((row) => (
              <TableRow key={row.id} onClick={() => handleRowClick(row)} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell>{row.address}<br />{row.address2}</TableCell>
                <TableCell>{`${row.city}, ${row.state} ${row.zip}`}</TableCell>
                <TableCell align="right">
                    <IconButton 
                      edge="end" 
                      aria-label="delete" 
                      onClick={(e) => handleDeleteClick(e, row)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table> 
      </TableContainer>
      )}
      <AddOrEditDialog
        open={dialogOpen}
        handleClose={handleDialogClose}
        handleSave={handleSave}
        initialData={currentData}
        allData={addresses}
      />
      <ExportDialog
        open={exportDialogOpen}
        handleClose={() => setExportDialogOpen(false)}
        addresses={filteredAddresses}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmationOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Address?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the address for <strong>{addressToDelete?.name}</strong>?
            <br/>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      </Container>  
    </PageWrapper>
    
  );
}

export default FamilyAddressTable;
